import type { StoreProduct } from "replo-runtime/shared/types";

import * as React from "react";

import { isFeatureEnabled } from "@editor/infra/featureFlags";
import { setProductData } from "@editor/reducers/commerce-reducer";
import { useEditorStore } from "@editor/store";
import { mapProductToStoreProduct } from "@editor/utils/product";
import { trpc } from "@editor/utils/trpc";
import useCurrentProjectId from "@hooks/useCurrentProjectId";

import { skipToken } from "@tanstack/react-query";
import { filterNulls } from "replo-utils/lib/array";

/**
 * Hook to fetch product data for the current store.
 */
export function useInfiniteStoreProducts(opts: {
  pageSize: number;
  searchTerm?: string;
}) {
  const { pageSize, searchTerm } = opts;
  const projectId = useCurrentProjectId();
  const { dispatch } = useEditorStore();

  const queryResult = trpc.product.list.useInfiniteQuery(
    projectId
      ? {
          projectId,
          limit: pageSize,
          writeThroughProductsSync: isFeatureEnabled(
            "write-through-products-sync",
          ),
          filters: {
            searchTerm: searchTerm ?? "",
          },
        }
      : skipToken,
    {
      enabled: Boolean(projectId),
      getNextPageParam: ({ nextCursor }) => nextCursor,
    },
  );

  const products = React.useMemo((): StoreProduct[] => {
    const allProducts =
      queryResult.data?.pages?.flatMap((page) => page.products) ?? [];
    return filterNulls(allProducts.map(mapProductToStoreProduct));
  }, [queryResult.data]);

  // Note (Kevin, 2025-06-20): Maintain Redux side effect for components that depend on product data store
  // This replicates RTK Query's automatic Redux updates on query success
  React.useEffect(() => {
    if (products.length > 0) {
      dispatch(setProductData(products));
    }
  }, [products, dispatch]);

  return {
    ...queryResult,
    products,
  };
}
